<template>
  <section class="tolp" aria-label="Applied tolerance">
    <header class="tolp__head">
      <span class="tolp__cap">Tolerance</span>
      <!-- Reads the APPLIED tolerance, so it must not turn red for a partial
           draft in the fields below — the applied value is still valid. The
           per-field border carries the draft's validity. -->
      <span class="tolp__value">{{ summaryText }}</span>
    </header>

    <!-- Provenance. DERIVED from the current values, never remembered: a rule
         that was picked and then hand-edited must stop claiming to be that
         rule, or the report cites a spec the numbers no longer obey. -->
    <p v-if="matchedRule" class="tolp__ref">
      <span class="tolp__ref-name">{{ matchedRule.name }}</span>
      <span v-for="(meta, i) in matchedRuleMeta" :key="`meta-${i}`" class="tolp__ref-meta">
        <span class="tolp__ref-sep" aria-hidden="true">·</span>{{ meta }}
      </span>
    </p>
    <p v-else class="tolp__ref tolp__ref--none">
      Hand-entered — no library rule
    </p>

    <div class="tolp__fields" role="group" aria-label="Per-axis tolerance in millimetres">
      <v-text-field
        height="32px"
        v-for="field in axisFields"
        :key="field.key"
        v-model="fields[field.key]"
        :label="field.axis"
        type="text"
        inputmode="decimal"
        prefix="±"
        suffix="mm"
        outlined
        dense
        hide-details
        class="tolp__input"
        :error="!axisValid[field.key]"
        :aria-label="field.label"
        :aria-invalid="axisValid[field.key] ? 'false' : 'true'"
        :disabled="disabled"
        @change="onAxisCommit(field.key)"
      />
    </div>

    <div class="tolp__actions">
      <button
        type="button"
        class="tolp__btn"
        :disabled="disabled || !axisValid.x"
        title="Copy the X tolerance onto Y and Z"
        @click="applyXToAllAxes"
      >Apply X to all axes</button>

      <button
        type="button"
        class="tolp__btn tolp__btn--swap"
        :disabled="disabled || !axisValid.x || !axisValid.y"
        aria-label="Swap X and Y tolerances"
        title="Swap the X and Y tolerance values"
        @click="swapXYTolerances"
      >X ↔ Y</button>

      <v-menu
        v-model="libraryOpen"
        offset-y
        :close-on-content-click="true"
        content-class="tolp-menu-popover"
      >
        <template v-slot:activator="{ on, attrs }">
          <button
            type="button"
            class="tolp__btn tolp__btn--combo"
            v-bind="attrs"
            :aria-expanded="libraryOpen ? 'true' : 'false'"
            aria-haspopup="listbox"
            :disabled="disabled || !hasLibrary"
            :title="hasLibrary ? 'Choose a tolerance from the library' : 'Tolerance library unavailable'"
            v-on="on"
          >
            <span>Library</span>
            <span class="tolp__chev" aria-hidden="true">
              <svg viewBox="0 0 10 10"><path d="M2 4l3 3 3-3" /></svg>
            </span>
          </button>
        </template>

        <div class="tolp-menu" role="listbox" aria-label="Tolerance library">
          <button
            v-for="rule in libraryRules"
            :key="rule.id"
            type="button"
            class="tolp-menu__item"
            :class="{ on: matchedRule && matchedRule.id === rule.id }"
            role="option"
            :aria-selected="matchedRule && matchedRule.id === rule.id ? 'true' : 'false'"
            @click="applyRule(rule)"
          >
            <span class="tolp-menu__name">{{ rule.name }}</span>
            <span class="tolp-menu__val">{{ ruleValueText(rule) }}</span>
            <span v-if="ruleMetaText(rule)" class="tolp-menu__meta">{{ ruleMetaText(rule) }}</span>
          </button>
        </div>
      </v-menu>
    </div>
  </section>
</template>

<script>
/**
 * AVA-475 — Applied-tolerance picker (workflow Step 5).
 *
 * Three per-axis numeric fields in MILLIMETRES, a one-shot "apply X to all
 * axes" convenience, and a library lookup that fills all three at once.
 *
 * DOMAIN RULES honoured here:
 * - Per-axis only. The library fills three fields; it does not lock them
 *   together, because Y and Z must be able to stay PASS while X fails.
 * - UNITS: tolerances and deltas are MILLIMETRES. Positions are NOT — they are
 *   VIEWER units, which are metres on some facilities and millimetres on others
 *   (utils/modelUnits.js); `pointMath.computeDeltas` crosses between the two
 *   using the model's resolved `mmPerViewerUnit`, not a constant. Nothing in
 *   this file touches a position, and nothing in it should: a millimetre here is
 *   always a millimetre, on every facility.
 * - PROVENANCE IS DERIVED, NEVER REMEMBERED. `matchedRule` is looked up from
 *   the current values on every read, so hand-editing an axis immediately
 *   drops the library citation instead of leaving a conformance report
 *   claiming a spec its numbers no longer satisfy.
 *
 * This is a leaf: it reads no store and dispatches nothing. It emits `change`
 * with the exact `tolerance` payload `VERIFICATION/setTolerance` expects, and
 * the parent (which knows which point it rendered this for) supplies the id.
 */
import { formatTolerance } from '@/features/Verification/utils/checkStatusDisplay';
import { log as debugLog } from '@/utils/debugLogger';
// Authored in parallel (Task 6 Step 1). Assumed named export of an array of
// frozen rule entries; `libraryRules` degrades to [] on any other shape.
import {
  toleranceLibraryMock,
  findMatchingToleranceEntry,
} from '@/features/Verification/data/toleranceLibraryMock';

const AXIS_FIELDS = Object.freeze([
  Object.freeze({ key: 'x', axis: 'X', label: 'X tolerance in millimetres' }),
  Object.freeze({ key: 'y', axis: 'Y', label: 'Y tolerance in millimetres' }),
  Object.freeze({ key: 'z', axis: 'Z', label: 'Z tolerance in millimetres' }),
]);

const UNIT_MM = 'mm';

// Optional traceability fields, in the order they read on the report line.
// These are the field names `data/toleranceLibraryMock.js` actually exports —
// reconciled at wiring time (Task 6 Step 4). The earlier placeholder names
// (`spec` / `revision` / `rulePack`) resolved to undefined on every entry and
// were silently dropped by the `.filter(Boolean)` below, so the one entry that
// carries a real citation (`tol-hpc-pos`) showed no provenance at all.
// Order matters: the pack must precede its own revision, or the line reads
// "Rev B" before the thing Rev B revises.
const RULE_META_KEYS = Object.freeze([
  'sourceDoc',
  'rulePackName',
  'rulePackRevision',
  'discipline',
]);

/**
 * Parse a field value to a finite number. Returns null for anything not fully
 * numeric — including '' (which `Number` coerces to 0) and partial input such
 * as '-' or '1.'. This is why the inputs are `type="text" inputmode="decimal"`
 * and not `type="number"`, which silently blanks partial input mid-typing.
 */
function toNumber(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/** A tolerance is a half-width: zero means "exact", negative is meaningless. */
function toTolerance(value) {
  const parsed = toNumber(value);
  if (parsed === null || parsed < 0) return null;
  return parsed;
}

function toField(value) {
  return value === null || value === undefined ? '' : String(value);
}

export default {
  name: 'TolerancePicker',
  props: {
    // Applied tolerance for one point, in MILLIMETRES.
    tolerance: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // String mirrors of the prop — the fields must hold partial input while
      // the surveyor types without writing garbage back to the store.
      fields: { x: '', y: '', z: '' },
      libraryOpen: false,
    };
  },
  computed: {
    axisFields() {
      return AXIS_FIELDS;
    },
    libraryRules() {
      return Array.isArray(toleranceLibraryMock) ? toleranceLibraryMock : [];
    },
    hasLibrary() {
      return this.libraryRules.length > 0;
    },
    parsed() {
      return {
        x: toTolerance(this.fields.x),
        y: toTolerance(this.fields.y),
        z: toTolerance(this.fields.z),
      };
    },
    axisValid() {
      return {
        x: this.parsed.x !== null,
        y: this.parsed.y !== null,
        z: this.parsed.z !== null,
      };
    },
    allValid() {
      return AXIS_FIELDS.every((f) => this.axisValid[f.key]);
    },
    /** Uniform tolerances read as one figure; mixed ones must show all three. */
    summaryText() {
      const applied = this.tolerance || {};
      const { x, y, z } = applied;
      if ([x, y, z].some((v) => v === null || v === undefined)) return '—';
      if (x === y && y === z) return formatTolerance(x, UNIT_MM);
      return `±${x} / ±${y} / ±${z} ${UNIT_MM}`;
    },
    /**
     * The library entry whose three axes exactly equal the applied tolerance,
     * or null. Derived — see the component docblock.
     */
    matchedRule() {
      // The library owns its own matching rule. Re-implementing the triple
      // comparison here was a second definition of "is this the same tolerance"
      // that would drift the moment the library gains a field — a per-axis
      // asymmetric tolerance, say — that an === on x/y/z cannot see.
      return findMatchingToleranceEntry(this.tolerance) || null;
    },
    /** Optional entry fields only — a minimal `{id,name,tolerance}` still renders. */
    matchedRuleMeta() {
      if (!this.matchedRule) return [];
      return RULE_META_KEYS.map((key) => this.matchedRule[key]).filter(Boolean);
    },
  },
  watch: {
    // Resyncs when the parent switches point, and after our own emit round-trips
    // back through the store, so the fields never drift from the applied value.
    tolerance: {
      immediate: true,
      deep: true,
      handler(next) {
        this.fields = {
          x: toField(next && next.x),
          y: toField(next && next.y),
          z: toField(next && next.z),
        };
      },
    },
  },
  methods: {
    ruleValueText(rule) {
      const t = rule && rule.tolerance;
      if (!t) return '—';
      if (t.x === t.y && t.y === t.z) return formatTolerance(t.x, UNIT_MM);
      return `±${t.x} / ±${t.y} / ±${t.z} ${UNIT_MM}`;
    },
    ruleMetaText(rule) {
      if (!rule) return '';
      return RULE_META_KEYS.map((key) => rule[key]).filter(Boolean).join(' · ');
    },
    /**
     * Commits on `v-text-field`'s forwarded native `change` event (blur /
     * Enter), NOT on `input`. Emitting per keystroke round-trips through the
     * store, and the `tolerance` watcher then rewrites the field the surveyor
     * is still typing into:
     * typing "0.001" over "3" emits at the very first character because "0" is
     * itself a legal tolerance, and the resync corrupts the rest of the entry.
     * v-model still updates `fields` on every keystroke, so the per-field
     * validity border stays live.
     */
    onAxisCommit(axisKey) {
      debugLog('[TolerancePicker] axis committed', axisKey, this.fields[axisKey]);
      this.emitIfChanged();
    },
    applyXToAllAxes() {
      // One-shot, not a sticky link mode: a per-axis edit must never be
      // silently mirrored onto the other two axes afterwards.
      if (this.disabled || !this.axisValid.x) return;
      const value = this.fields.x;
      this.fields = { x: value, y: value, z: value };
      debugLog('[TolerancePicker] applied X to all axes', value);
      this.emitIfChanged();
    },
    swapXYTolerances() {
      if (this.disabled || !this.axisValid.x || !this.axisValid.y) return;
      const { x, y, z } = this.fields;
      this.fields = { x: y, y: x, z };
      debugLog('[TolerancePicker] swapped X and Y tolerances', this.fields);
      this.emitIfChanged();
    },
    applyRule(rule) {
      const t = rule && rule.tolerance;
      if (this.disabled || !t) return;
      this.fields = { x: toField(t.x), y: toField(t.y), z: toField(t.z) };
      debugLog('[TolerancePicker] library rule applied', rule.id, t);
      this.emitIfChanged();
    },
    /**
     * Emits only a complete, valid, genuinely different triple. Partial input
     * ('1.', '-') and no-op edits are swallowed so the store is not churned
     * on every keystroke and every point's verdicts are not recomputed for
     * a value that did not move.
     */
    emitIfChanged() {
      if (!this.allValid) return;
      const next = { x: this.parsed.x, y: this.parsed.y, z: this.parsed.z };
      const applied = this.tolerance || {};
      if (next.x === applied.x && next.y === applied.y && next.z === applied.z) return;
      debugLog('[TolerancePicker] change', next);
      this.$emit('change', next);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.tolp {
  padding: 12px;
  border: 1px solid $vf-line-soft;
  border-radius: $vf-r-md;
  background: $vf-panel-2;
  box-shadow: $vf-shadow-1;

  &__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: $vf-sm;
  }

  &__cap {
    font-family: $vf-font-mono;
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $vf-ink-3;
  }

  &__value {
    font-family: $vf-font-mono;
    font-size: 13px;
    font-weight: 600;
    color: $vf-ink;

    &--invalid {
      color: $vf-error;
    }
  }

  &__ref {
    margin: $vf-xs 0 0;
    font-size: 11px;
    line-height: 1.4;
    color: $vf-ink-2;

    &--none {
      color: $vf-ink-3;
      font-style: italic;
    }
  }

  &__ref-name {
    font-weight: 500;
    color: $vf-ink;
  }

  &__ref-meta {
    font-family: $vf-font-mono;
    font-size: 10.5px;
  }

  &__ref-sep {
    margin: 0 5px;
    color: $vf-ink-3;
  }

  &__fields {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: $vf-sm;
    margin-top: 10px;
  }

  &__input {
    width: 100%;
    min-width: 0;
    min-height: 32px;

    ::v-deep .v-input__control {
      min-width: 0;
    }

    ::v-deep .v-input__slot {
      min-height: 32px !important;
      background: #fff;
    }

    ::v-deep input {
      min-width: 0;
      font-family: $vf-font-mono;
      font-size: 12px;
      text-align: right;
    }

    ::v-deep .v-text-field__prefix,
    ::v-deep .v-text-field__suffix {
      color: $vf-ink-3;
      font-family: $vf-font-mono;
      font-size: 11px;
    }

    ::v-deep .v-text-field__suffix {
      font-size: 10px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 10px;
  }

  &__btn {
    height: 26px;
    padding: 0 10px;
    border: 1px solid $vf-line;
    border-radius: $vf-r-sm;
    background: #fff;
    color: $vf-ink-2;
    font-family: $vf-font-mono;
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: $vf-line-strong;
      color: $vf-ink;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--combo {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-left: auto;
    }

    &--swap {
      flex: none;
      padding: 0 5px;
      letter-spacing: 0;
    }
  }

  &__chev svg {
    width: 10px;
    height: 10px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.4;
  }
}
</style>

<style lang="scss">
/* Unscoped ON PURPOSE and deliberately narrow: `content-class` is applied by
   Vuetify to its own teleported `.v-menu__content` element, which never
   carries this component's scope attribute. Same trap documented in
   DesignPointModal.vue's unscoped block. */
@import '../../styles/verification-tokens';

.tolp-menu-popover {
  box-shadow: $vf-shadow-3;
  border-radius: $vf-r-sm;
}

.tolp-menu {
  background: $vf-panel-2;
  border: 1px solid $vf-line;
  border-radius: $vf-r-sm;
  padding: 4px 0;
  max-height: 260px;
  overflow-y: auto;

  &__item {
    display: block;
    width: 100%;
    padding: 6px 12px;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: rgba(3, 98, 76, 0.06);
    }

    &.on {
      background: rgba(3, 98, 76, 0.1);
    }
  }

  &__name {
    display: block;
    font-size: 12px;
    color: $vf-ink;
  }

  &__val {
    display: block;
    font-family: $vf-font-mono;
    font-size: 11px;
    color: $vf-ink-2;
  }

  &__meta {
    display: block;
    font-family: $vf-font-mono;
    font-size: 10px;
    color: $vf-ink-3;
  }
}
</style>
